import { Injectable } from '@angular/core';
import { OdvDocuments } from '@shared/entities/odvDocuments';
import { ApiService } from '@shared/services';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Attachment } from '@shared/entities/attachment';

const LOG_PREFIX = '[Api][OdvDocumentsForm] ';

@Injectable({
  providedIn: 'root'
})
export class OdvDocumentsFormService extends ApiService<OdvDocuments> {
  constructor(private http: HttpClient) {
    const masterUrl = '/odv-documents';
    const detailUrl = '/odv-documents';
    super(OdvDocuments, masterUrl, detailUrl, undefined, 'odv-documents-form');
  }

  findOdvDocument(id: string): Observable<OdvDocuments | null> {
    return this.findOne(id).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'findOdvDocument error', error);
        return of(null);
      })
    );
  }

  saveOdvDocumentWithAttachmentId(documentData: OdvDocuments): Observable<OdvDocuments> {
    this.removeUrlParameters();

    return this.save(documentData).pipe(
      tap((result) => {
        this.logger.debug(LOG_PREFIX + 'saveOdvDocumentWithAttachmentId success', result);
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'saveOdvDocumentWithAttachmentId error', error);
        throw error;
      })
    );
  }

  saveOdvDocumentWithAttachment(documentData: OdvDocuments, file: File): Observable<OdvDocuments> {
    this.removeUrlParameters();

    const formData = new FormData();
    formData.append('request', JSON.stringify(documentData));
    formData.append('document', file);

    return this.postMultipartData<OdvDocuments>('/odv-documents', formData, OdvDocuments).pipe(
      tap((result) => {
        this.logger.debug(LOG_PREFIX + 'saveOdvDocumentWithAttachment success', result);
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'saveOdvDocumentWithAttachment error', error);
        throw error;
      })
    );
  }

  updateOdvDocumentWithAttachment(
    documentId: string,
    documentData: OdvDocuments,
    file: File
  ): Observable<OdvDocuments> {
    this.removeUrlParameters();

    const formData = new FormData();
    documentData.id = documentId;
    formData.append('request', JSON.stringify(documentData));
    formData.append('document', file);

    return this.putMultipartData<OdvDocuments>('/odv-documents', formData, OdvDocuments).pipe(
      tap((result) => {
        this.logger.debug(LOG_PREFIX + 'updateOdvDocumentWithAttachment success', result);
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'updateOdvDocumentWithAttachment error', error);
        throw error;
      })
    );
  }

  findByFilter(id: string): Observable<OdvDocuments | null> {
    this.setUrlParameter('id', id);

    return this.paginate(OdvDocuments, 1, 1).pipe(
      map((paginate) => {
        if (paginate && paginate.data && paginate.data.length > 0) {
          return paginate.data[0];
        }
        return null;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'findByFilter error', error);
        return of(null);
      })
    );
  }

  deleteOdvDocument(id: string): Observable<boolean | null> {
    this.removeUrlParameters();
    return this.remove(id).pipe(
      map(() => {
        return true;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'deleteOdvDocument error', error);
        return of(error);
      })
    );
  }

  uploadAttachment(file: File | FormData): Observable<Attachment> {
    this.removeUrlParameters();

    const formData = this.createFormData(file);

    return this.postMultipartData<Attachment>('/attachments', formData, Attachment).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'uploadAttachment error', error);
        throw error;
      })
    );
  }

  deleteFile(fileId: string): Observable<boolean | null> {
    return this.http.delete<boolean>(`/attachments/${fileId}`).pipe(
      map(() => {
        return true;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'deleteFile error', error);
        return of(null);
      })
    );
  }

  private createFormData(fileOrFormData: File | FormData): FormData {
    if (fileOrFormData instanceof FormData) {
      return fileOrFormData;
    }

    const formData = new FormData();
    formData.append('file', fileOrFormData, fileOrFormData.name);
    return formData;
  }
}
